import React from 'react';
import Grid from '@mui/material/Grid';
import SpeedAudit from 'react-scale/components/audits/SpeedAudit';
import { IUserJson } from '@scale/shared/types/user';
import SAlert from 'react-s-alert';
import _ from 'lodash';
import { IAdditionalOperationParametersProps } from './sharedTypes';
import { Checkbox, Option, Select, TextInput } from 'react-scale/components/design-system';
import { Button } from '@mui/material';
import { AuditViewType } from '@scale/shared/types/corp/attemptAudit';

const NewSpeedAuditBatchForm: React.FC<{
  userId: string;
  name: string;
  onSubmit: () => void;
  setRelatedObjectIdList: React.Dispatch<React.SetStateAction<string[]>>;
}> = ({ userId, setRelatedObjectIdList, name, onSubmit }) => {
  const user: IUserJson = { _id: userId } as any;
  const onBatchId = React.useCallback(
    (batchId: string) => {
      setRelatedObjectIdList(prev => _.uniq([...prev, batchId]));
      SAlert.success(`Successfully added Batch ID ${batchId}`);
      onSubmit();
    },
    [setRelatedObjectIdList],
  );
  return (
    <Grid item xs={12}>
      <Button color="secondary" onClick={onSubmit}>
        {' '}
        Cancel{' '}
      </Button>
      <SpeedAudit
        showProjectChooser
        user={user}
        qaOperationName={name}
        hideAdvancedFilters
        createQAOperationDialogMode
        hideVersionChoice
        onGetOrCreateBatchOverride={onBatchId}
      />
    </Grid>
  );
};

const SpeedAuditBatchSize: React.FunctionComponent<IAdditionalOperationParametersProps> = props => {
  const { setAdditionalParams } = props;
  const [isCustomBatchSize, setIsCustomBatchSize] = React.useState<boolean>(false);
  const [operationBatchSizeStr, setOperationBatchSizeStr] = React.useState<string>('');
  const onBatchSizeChange = React.useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    setOperationBatchSizeStr(evt.target.value);
  }, []);

  React.useEffect(
    () =>
      setAdditionalParams(prev => {
        const intSize = parseInt(operationBatchSizeStr);
        const validBatchSize = !isNaN(intSize) && intSize > 0;
        const batchSize = isCustomBatchSize && validBatchSize ? intSize : undefined;
        return { ...prev, batchSize };
      }),
    [isCustomBatchSize, operationBatchSizeStr],
  );

  return (
    <Grid item xs={3}>
      <Checkbox
        label="Split into fixed size batches?"
        checked={isCustomBatchSize}
        onChange={evt => setIsCustomBatchSize(evt.target.checked)}
      />
      <br />
      {isCustomBatchSize && (
        <TextInput
          placeholder="Enter number of items per batch"
          fullWidth
          value={operationBatchSizeStr}
          onChange={onBatchSizeChange}
        />
      )}
    </Grid>
  );
};

const SpeedAuditViewTypeParam: React.FunctionComponent<
  IAdditionalOperationParametersProps
> = props => {
  const { setAdditionalParams } = props;
  const [auditViewType, setAuditViewType] = React.useState<AuditViewType>(AuditViewType.STANDARD);
  const onViewTypeChange = React.useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    setAuditViewType(evt.target.value as AuditViewType);
  }, []);

  React.useEffect(() => {
    setAdditionalParams(prev => ({ ...prev, auditBatchView: auditViewType }));
  }, [auditViewType]);

  return (
    <Grid item xs={3}>
      <h4 style={{ display: 'inline-block', marginRight: '1ex' }}>Audit Type:</h4>
      <Select fullWidth onChange={onViewTypeChange} value={auditViewType}>
        <Option value={AuditViewType.STANDARD}>Standard</Option>
        <Option value={AuditViewType.TEXT_BASED}>Text Based</Option>
        <Option value={AuditViewType.CHAT_BULK_AUDIT}>Chat Bulk Audit (For Chat Tasks Only)</Option>
      </Select>
    </Grid>
  );
};

export const SpeedAuditAdditionalParams: React.FunctionComponent<
  IAdditionalOperationParametersProps
> = props => {
  const [toggleCreateSpeedAudit, setToggleCreateSpeedAudit] = React.useState<boolean>(false);
  return (
    <>
      {toggleCreateSpeedAudit ? (
        <NewSpeedAuditBatchForm {...props} onSubmit={() => setToggleCreateSpeedAudit(false)} />
      ) : (
        <Grid item xs={3} style={{ marginTop: '1ex' }}>
          <br />
          <i style={{ fontWeight: 'bolder', marginRight: '1ex', marginLeft: '-2vh' }}>Or... </i>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setToggleCreateSpeedAudit(true)}
          >
            {' '}
            Create new speed audit batch{' '}
          </Button>
        </Grid>
      )}

      <SpeedAuditBatchSize {...props} />
      <SpeedAuditViewTypeParam {...props} />
    </>
  );
};
